import { useContext } from 'react';
import styled, { css } from 'styled-components';
import { Headline500Skin } from '../../atoms/Headline/HeadlineSkins';
import Col from '~/grid/Col';
import Row from '~/grid/Row';
import CTA from '../../atoms/CTA';
import { mq } from '~/styles/grid';
import Container from '~/grid/Container';
import { CTAIconPosition } from '../../atoms/CTA/CTA.constants';
import { CopyStandardSkin } from '../../atoms/Copytext/CopytextSkins';
import { getColorByName } from '~/helpers/getColors';
import { LanguageContext } from '~/helpers/LanguageContext';
import { addQuotes } from './Quote.helpers';
import ContentHubQuotation from '~/components/atoms/Icon/icons/ContentHubQuotation';
import Picture from '../../atoms/Picture';
import getImageProperties from '~/helpers/getImageProperties';

const overHangMobile = 87;
const overhangDesktop = 44;
const paddingY = 30;

const Wrapper = styled.div`
  width: 100%;
  background: linear-gradient(
    90deg,
    rgba(255, 255, 255, 1) 0%,
    rgba(255, 255, 255, 1) 50%,
    ${(props) => props.theme.color.gray50} 50.1%,
    ${(props) => props.theme.color.gray50} 100%
  );

  margin-top: ${overhangDesktop}px;
  @media (${mq.mdDown}) {
    background: ${(props) => props.theme.color.gray50};
  }

  @media (${mq.smDown}) {
    margin-top: ${overHangMobile}px;
  }
`;

const StyledContainer = styled(Container)`
  background: ${(props) => props.theme.color.gray50};
`;

const QuoteContainer = styled(Row)`
  background: ${(props) => props.theme.color.gray50};
  display: flex;
  justify-content: flex-start;
  padding: ${paddingY}px 4%;
  margin-top: 0;
  margin-bottom: 0;

  @media (${mq.xl}) {
    margin: auto;
    flex-wrap: nowrap;
    max-width: 100%;
  }

  @media (${mq.xs}) {
    flex-direction: column;
    padding: 20px 4%;
  }
`;
const ImageContainer = styled(Col)`
  margin-top: -${paddingY + overhangDesktop}px;
  margin-right: 57px;

  @media (${mq.smDown}) {
    margin-right: 25px;
    margin-left: -8px;
  }

  @media (${mq.xs}) {
    margin-right: 0;
    margin-left: 0;
    margin-top: -${paddingY + overHangMobile}px;
    margin-bottom: 20px;
    align-self: center;
    display: flex;
    justify-content: center;
  }
  svg {
    max-height: 164px;
  }
`;

const PictureStyled = styled(Picture)`
  img {
    max-height: 300px;
  }
`;
const TextContainer = styled(Col)`
  display: block;
  ${({ hasImage }) =>
    !hasImage &&
    css`
      width: 100%;
    `}
`;

const QuoteElement = styled.div`
  ${Headline500Skin};
  display: block;
  margin-bottom: 27px;
  @media (${mq.smDown}) {
    font-size: ${(props) => props.theme.headline.h500.xs.fontSize};
    line-height: ${(props) => props.theme.headline.h500.xs.lineHeight};
  }
`;

const NameTitleElement = styled.span`
  ${CopyStandardSkin};
  display: block;
  margin: 0;
  color: ${(props) =>
    getColorByName(
      props.theme,
      props.theme.copytext.copyStandard.textColor,
    )} !important;
  &:last-of-type {
    margin-bottom: 17px;
  }
`;

const CTAStyled = styled(CTA)`
  padding: 0;
  & span,
  & span svg {
    color: ${(props) => props.theme.color.primary800} !important;
  }
`;

/**
 * @param {ImageProps} image - The image properties for the quote.
 * @returns {Cols} - The column configuration for the text container based on the presence of
 */
const getTextColAmount = (image) => {
  if (image) {
    return { xl: 8, lg: 8, md: 8, sm: 8.55, xs: 12 };
  }
  return { xl: 9, lg: 10, md: 12, sm: 12, xs: 12 };
};

/**
 * @param {QuoteProps} props
 * @returns {React.ReactElement} The rendered component
 */
const Quote = ({ image, quote, name, title, link }) => {
  const lang = useContext(LanguageContext);
  return (
    <Wrapper>
      <StyledContainer>
        <QuoteContainer>
          {image && (
            <ImageContainer
              colAmount={{ xl: 3.3, lg: 3.2, md: 3.2, sm: 3.6, xs: 8 }}
            >
              {!['Inverted commas', 'Quote Icon'].includes(image.alt) ? (
                <PictureStyled
                  urls={getImageProperties(
                    { xl: image.url, md: image.url, xs: image.url },
                    'quote',
                  )}
                  alt={image.alt || ''}
                  title={image.title || ''}
                />
              ) : (
                <ContentHubQuotation />
              )}
            </ImageContainer>
          )}
          <TextContainer hasImage={!!image} colAmount={getTextColAmount(image)}>
            <QuoteElement>{addQuotes({ quote, language: lang })}</QuoteElement>
            {name && <NameTitleElement>{`${name},`}</NameTitleElement>}
            {title && <NameTitleElement>{title}</NameTitleElement>}
            {link && (
              <CTAStyled
                skin="TERTIARY"
                icon="ARROW_ANIMATION"
                iconPos={CTAIconPosition.Before}
                href={link.href}
                target={link.target || '_blank'}
                title={link.title || link.text}
                text={link.text}
              />
            )}
          </TextContainer>
        </QuoteContainer>
      </StyledContainer>
    </Wrapper>
  );
};

export default Quote;
